import { notImplemented, mod97CheckSum } from "../utils/general-helper";
import { IRegionFunctions, ParsedAccount } from "./region-functions";

interface SimpleRegionConfig {
	/** Country code for phone numbers (e.g., "+33" for France, "+31" for Netherlands, "+32" for Belgium) */
	countryCode: string;
	/** Prefix for phone without country code (e.g., "0" for most countries) */
	phonePrefix?: string;
}

/**
 * Factory function to create region functions for countries without clearing numbers
 * Typically used for countries such as: France (FR), Belgium (BE), Netherlands (NL) and similar cases.
 */
export const createSimpleRegionFunctions = (config: SimpleRegionConfig): IRegionFunctions => {
	const { countryCode, phonePrefix = "0" } = config;

	// Phone number format: +countryCode or phonePrefix(0), followed by area code and number
	// Pattern: country code, then digit groups with optional formatting
	const validPhoneNrExpression = RegExp(`^(\\${countryCode}|${phonePrefix})\\d{1,3}[- ]?\\d{3}[- ]?\\d{4}$`);
	const basicIbanStructureRegEx = RegExp(/^(?=.{15,34}$)[A-Z]{2}[A-Z0-9]+$/);

	const accountNumberMaxLength = 50;

	const validPhoneNumber = (phoneNumber: string): boolean => validPhoneNrExpression.test(phoneNumber);

	const formatPhoneNumber = (_phoneNumber: string): string => {
		return notImplemented();
	};

	const validateAccountNumber = (accountNumber: string): boolean => {
		if (accountNumber.length < 1) return true;
		return accountNumber.length > 0 && accountNumber.length <= accountNumberMaxLength;
	};

	const getBankAccountMeta = (_accountNrIncludingClearing?: string) => {
		return {
			clearingNumberLength: 0,
			accountNumberMaxLength,
		};
	};

	const parseAccountNumber = (accountNrIncClearing: string): ParsedAccount => {
		return { clearingNumber: "", accountNumber: accountNrIncClearing };
	};

	const formatAccountNumber = (_clearingNr: string, accountNr: string) => {
		return accountNr;
	};

	const validateIban = (IBAN: string, allowedCountryCodes?: string): boolean => {
		if (IBAN.length < 1) return true;
		if (allowedCountryCodes && allowedCountryCodes !== "") {
			const ibanCountryCode = IBAN.substring(0, 2);
			if (!allowedCountryCodes.split(",").some((countryCode) => countryCode === ibanCountryCode))
				return false;
		}
		IBAN = IBAN.replace(/[\s]+/g, "").toUpperCase();
		if (!basicIbanStructureRegEx.test(IBAN)) return false;

		const rearrangedIban = IBAN.slice(4) + IBAN.slice(0, 4);
		const convertedIban = rearrangedIban.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString());

		return mod97CheckSum(convertedIban);
	};

	return {
		validateAccountNumber,
		validPhoneNumber,
		formatPhoneNumber,
		parseAccountNumber,
		validateIban,
		formatAccountNumber,
		bankAccountMeta: getBankAccountMeta,
	};
};
